import React, { useState } from "react";
import Modal from "style-guide/component/Modal";
import Input from "style-guide/component/Input";
import TextArea from "style-guide/component/Textarea";
import Button from "style-guide/component/Button";
import Typography from "style-guide/component/Typography";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "redux/store";
import { FormData, FormErrors } from "types/subscription";
import { contactSales } from "../../redux/slice/rfpModuleSlice/application";

interface DemoRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  modalType: "demo" | "sales";
}

const DemoRequestModal: React.FC<DemoRequestModalProps> = ({
  isOpen,
  onClose,
  modalType,
}) => {
  const { userProfile } = useSelector(
    (state: RootState) => state.applicationData
  );
  const userDetails = userProfile?.userDetails?.[0];
  const dispatch = useDispatch<AppDispatch>();
  const [formData, setFormData] = useState<FormData>({
    firstName: userDetails?.firstName || "",
    lastName: userDetails?.lastName || "",
    email: userDetails?.email || "",
    company: userDetails?.companyName || "",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const modalTitle = "Talk to Sales";
  const modalDescription =
    modalType === "demo"
      ? "Connect with our sales team to discuss your specific needs."
      : "Connect with our sales team to discuss your specific needs and find the perfect solution for your organization.";

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits except + at the beginning
    const cleaned = value.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");

    // If it starts with +, keep it, otherwise just digits
    if (cleaned.startsWith("+")) {
      return `+${cleaned.slice(1).replace(/\D/g, "")}`;
    }

    // Format US phone number pattern (adjust as needed)
    const digits = cleaned.replace(/\D/g, "");
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    if (digits.length <= 10)
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    let processedValue = value;

    if (field === "phone") {
      processedValue = formatPhoneNumber(value);
    }

    setFormData((prev) => ({ ...prev, [field]: processedValue }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
        formData.email.trim()
      )
    ) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.company.trim()) {
      newErrors.company = "Company name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await dispatch(contactSales(formData));
      if (result === 200) {
        setIsSubmitted(true);
        setFormData({
          firstName: userDetails?.firstName || "",
          lastName: userDetails?.lastName || "",
          email: userDetails?.email || "",
          company: userDetails?.companyName || "",
          phone: "",
          message: "",
        });
        // setIsSubmitted(false);
        // onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        firstName: userDetails?.firstName || "",
        lastName: userDetails?.lastName || "",
        email: userDetails?.email || "",
        company: userDetails?.companyName || "",
        phone: "",
        message: "",
      });
      setErrors({});
      setIsSubmitted(false);
      onClose();
    }
  };

  if (isSubmitted) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size="md"
        backdropClosable={false}
      >
        <div className="text-center py-8">
          <div className="mb-4">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>
          <Typography variant="Heading 3" className="text-gray-900 mb-2">
            Thank You!
          </Typography>
          <Typography
            variant="LG"
            className="text-gray-600 mb-6"
            text="Your request has been submitted. Our sales team will contact you within 24 hours to discuss your needs."
          />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle}
      size="lg"
      backdropClosable={!isSubmitting}
      showCloseIcon={!isSubmitting}
    >
      <div className="mb-6">
        <Typography variant="LG" className="text-gray-600">
          {modalDescription}
        </Typography>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              First Name *
            </label>
            <Input
              type="text"
              placeholder="Enter your first name"
              value={formData.firstName}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              disabled={isSubmitting}
              errorMessage={errors.firstName}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Name *
            </label>
            <Input
              type="text"
              placeholder="Enter your last name"
              value={formData.lastName}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              disabled={isSubmitting}
              errorMessage={errors.lastName}
              required
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email *
          </label>
          <Input
            type="email"
            value={formData.email}
            disabled
            errorMessage={errors.email}
            required
          />
        </div>

        {/* Company and Job Title */}
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company *
            </label>
            <Input
              type="text"
              placeholder="Enter your company name"
              value={formData.company}
              onChange={(e) => handleInputChange("company", e.target.value)}
              disabled={isSubmitting}
              errorMessage={errors.company}
              required
            />
          </div>
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number
          </label>
          <Input
            type="tel"
            placeholder="Enter your phone number"
            value={formData.phone}
            onChange={(e) => handleInputChange("phone", e.target.value)}
            disabled={isSubmitting}
            errorMessage={errors.phone}
          />
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Message *
          </label>
          <TextArea
            placeholder="Tell us about your organization's needs and requirements..."
            value={formData.message}
            onChange={(e) => handleInputChange("message", e.target.value)}
            disabled={isSubmitting}
            className="min-h-[100px]"
            required
          />
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <Button
            type={isSubmitting ? "Disabled" : "Primary"}
            buttonText={isSubmitting ? "Submitting..." : "Contact Sales"}
            className="w-full h-12"
          />
        </div>
      </form>
    </Modal>
  );
};

export default DemoRequestModal;
